import ModalHeader from '@molecules/ModalHeader';
import ModalWindow from '@organisms/modals/ModalWindow';
import {FC, useEffect, useState} from 'react';
import {
  ButtonsWrapper,
  ModalContent,
  ModalWrapper,
  SelectedAdminBlockWrapper,
  SelectedAdminInfoWrapper,
  SelectedAdminWrapper,
} from './AddSystemModal.styles';
import Button from '@atoms/Button';
import Input from '@atoms/Input';
import {useDispatch, useSelector} from 'react-redux';
import {users} from '@store/slices/users';
import {ISystem, IUser} from '@types';
import {addSystem, editSystem} from '@store/slices/systems';
import Select from '@atoms/Select';
import Text from '@atoms/Text';
import {theme} from '@styles/theme';
import Avatar from '@atoms/Avatar';
import {Close} from '@carbon/icons-react';
import {AppDispatch, RootState} from '@store';
import {toast} from 'react-toastify';
import Toast from '@atoms/Toast';
import {EUserRoles} from "@constants/usersRoles";

interface Props {
  isOpened: boolean;
  onCloseRequest: () => void;
  system?: ISystem;
}

const AddEditSystemModal: FC<Props> = ({isOpened, onCloseRequest, system}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [systemName, setSystemName] = useState<string>('');
  const [admin, setAdmin] = useState<string>('');
  const usersArr: IUser[] = useSelector(users);
  const userRole = useSelector((state: RootState) => state.profile.profile?.role_id);
  const adminOptions = usersArr.map(user => {
    return {
      title: user.username,
      subtitle: user.email,
      value: String(user.id),
      avatar: user.avatar_url,
    };
  });

  useEffect(() => {
    if (system?.name) {
      setSystemName(system.name);
    } else {
      setAdmin('');
      setSystemName('');
    }
  }, [system]);

  const AddSystem = () => {
    const currentAdmin = adminOptions.filter(option => option.value === admin);
    if (system?.id) {
      dispatch(
        editSystem({
          name: systemName,
          system_admin_id: currentAdmin[0].value,
          system_id: system.id,
        }),
      )
        .unwrap()
        .then(() => {
          toast.success(
            <Toast title="Готово!" text={'Система успешно изменена'} type="success" />,
          );
        })
        .catch((err: any) => {
          toast.error(
            <Toast
              title="Ошибка"
              text={err?.message || 'Не удалось отредактировать систему'}
              type="error"
            />,
          );
        });
    } else {
      dispatch(
        addSystem({
          name: systemName,
          system_admin_id: currentAdmin[0].value,
        }),
      )
        .unwrap()
        .then(() => {
          toast.success(
            <Toast title="Готово!" text={'Система успешно добавлена'} type="success" />,
          );
        })
        .catch((err: any) => {
          toast.error(
            <Toast
              title="Ошибка"
              text={err?.message || 'Не удалось добавить систему'}
              type="error"
            />,
          );
        });
    }
    setSystemName('');
    setAdmin('');
    onCloseRequest();
  };

  const currentAdmin = adminOptions.filter(option => option.value === admin);

  return (
    <ModalWindow isOpen={isOpened} overflowIsVisible>
      <ModalWrapper>
        <ModalHeader
          title={system?.id ? 'Редактировать' : 'Добавить систему'}
          onClose={onCloseRequest}
        />
        <ModalContent>
          <Input
            id="system-name"
            label="Название системы"
            value={systemName}
            onChange={e => {
              setSystemName(e.currentTarget.value);
            }}
            withoutErrorText
            hint="Максимум 50 символов"
            maxLength={50}
          />
          {!admin && userRole !== EUserRoles.BASE_USER && (
            <Select
              id="admin-name"
              label="Добавить ответственного администратора"
              value={0}
              options={adminOptions}
              onChange={setAdmin}
              withSearching
            />
          )}
          {!!admin && userRole !== EUserRoles.BASE_USER && (
            <SelectedAdminBlockWrapper>
              <Text
                text="Ответственный администратор"
                type="captionB"
                color={theme.colors.basic01}
              />
              <SelectedAdminWrapper>
                <SelectedAdminInfoWrapper>
                  <Avatar img={currentAdmin[0].avatar} />
                  <div>
                    <Text
                      text={currentAdmin[0].title}
                      type="tinyM"
                      color={theme.colors.basic01}
                    />
                    <Text
                      text={currentAdmin[0].subtitle}
                      type="tinyR"
                      color={theme.colors.basic02}
                    />
                  </div>
                </SelectedAdminInfoWrapper>
                <Close fill={theme.colors.basic03} onClick={() => setAdmin('')} />
              </SelectedAdminWrapper>
            </SelectedAdminBlockWrapper>
          )}
        </ModalContent>
        <ButtonsWrapper>
          <Button
            fullWidth
            title={system?.id ? 'Сохранить' : 'Добавить'}
            variant="filled"
            disabled={!systemName || !admin}
            onClick={AddSystem}
          />
          <Button
            fullWidth
            title="Закрыть"
            onClick={() => {
              onCloseRequest();
              setSystemName('');
              setAdmin('');
            }}
          />
        </ButtonsWrapper>
      </ModalWrapper>
    </ModalWindow>
  );
};

export default AddEditSystemModal;
